import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Send } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useCreateSupportTicket } from "@/api/supportTickets";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { FormActionBar } from "@/components/ui/form-action-bar";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { SubmitButton } from "@/components/ui/submit-button";
import { Textarea } from "@/components/ui/textarea";
import { requiredString } from "@/utils/formSchemas";

export const Route = createFileRoute("/_auth/app/_app/help/support/")({
	component: RouteComponent,
});

const supportSchema = z.object({
	type: z.enum(["request", "suggestion", "complaint"], {
		error: "forms.validation.required",
	}),
	title: requiredString().max(200),
	description: requiredString().max(2000),
});

const ticketTypes = [
	{
		value: "request",
		label: "help.supportTypeRequest",
		hint: "help.supportRequestHint",
	},
	{
		value: "suggestion",
		label: "help.supportTypeSuggestion",
		hint: "help.supportSuggestionHint",
	},
	{
		value: "complaint",
		label: "help.supportTypeComplaint",
		hint: "help.supportComplaintHint",
	},
] as const;

function RouteComponent() {
	const { t } = useTranslation();
	const mutation = useCreateSupportTicket();
	const [submitted, setSubmitted] = useState(false);
	const typeId = useId();
	const headingId = useId();
	const headingRef = useRef<HTMLHeadingElement>(null);
	const focusHeading = useRef(false);
	useEffect(() => {
		if (submitted || focusHeading.current) {
			headingRef.current?.focus();
			focusHeading.current = false;
		}
	}, [submitted]);
	const form = useForm<z.infer<typeof supportSchema>>({
		resolver: zodResolver(supportSchema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: { title: "", description: "" },
	});
	const submit = useGuardedSubmit(form, async (data) => {
		await mutation.mutateAsync({ data });
		form.reset();
		setSubmitted(true);
	});

	if (submitted) {
		return (
			<section className="mx-auto max-w-2xl py-12 sm:py-20">
				<CheckCircle2
					className="mb-6 size-10 text-primary"
					aria-hidden="true"
				/>
				<h2 ref={headingRef} tabIndex={-1} className="text-2xl font-semibold">
					{t("help.supportSuccessTitle")}
				</h2>
				<p className="mt-3 max-w-lg text-base-content/75">
					{t("help.supportSuccessDescription")}
				</p>
				<div className="mt-8 flex flex-col gap-3 sm:flex-row">
					<Button asChild>
						<Link to="/app/help/support/tickets">
							{t("help.myTickets")}
							<ArrowRight aria-hidden="true" />
						</Link>
					</Button>
					<Button
						variant="outline"
						onClick={() => {
							focusHeading.current = true;
							setSubmitted(false);
						}}
					>
						{t("help.supportAnotherTicket")}
					</Button>
				</div>
			</section>
		);
	}

	return (
		<div className="mx-auto grid max-w-6xl gap-8 py-6 lg:grid-cols-[minmax(0,1fr)_16rem] lg:gap-10 lg:py-8">
			<section
				className="min-w-0 rounded-xl border border-base-300 bg-base-100 p-5 sm:p-8"
				aria-labelledby={headingId}
			>
				<div className="mb-8">
					<h2
						id={headingId}
						ref={headingRef}
						tabIndex={-1}
						className="text-xl font-semibold sm:text-2xl"
					>
						{t("help.supportFormTitle")}
					</h2>
					<p className="mt-2 text-sm text-base-content/75">
						{t("help.supportFormIntro")}
					</p>
				</div>
				<Form {...form}>
					<form onSubmit={submit} noValidate className="space-y-7">
						<FormField
							control={form.control}
							name="type"
							render={({ field }) => (
								<FormItem>
									<FormLabel id={typeId} required>
										{t("help.supportFieldType")}
									</FormLabel>
									<FormControl>
										<RadioGroup
											aria-labelledby={typeId}
											aria-required="true"
											name={field.name}
											value={field.value ?? ""}
											onValueChange={field.onChange}
											onBlur={field.onBlur}
											disabled={mutation.isPending}
											className="gap-2 sm:grid-cols-3"
										>
											{ticketTypes.map((type, index) => (
												<label
													key={type.value}
													htmlFor={`${typeId}-${type.value}`}
													className="flex cursor-pointer items-start gap-3 rounded-lg border border-base-300 p-4 transition-colors hover:bg-base-200 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-primary"
												>
													<RadioGroupItem
														id={`${typeId}-${type.value}`}
														value={type.value}
														ref={index === 0 ? field.ref : undefined}
														className="mt-0.5 border-base-content/50 shadow-none"
													/>
													<span>
														<span className="block text-sm font-semibold">
															{t(type.label)}
														</span>
														<span className="mt-1 block text-sm text-base-content/75">
															{t(type.hint)}
														</span>
													</span>
												</label>
											))}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="title"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>{t("help.supportFieldTitle")}</FormLabel>
									<FormControl>
										<Input
											{...field}
											maxLength={200}
											disabled={mutation.isPending}
											placeholder={t("help.supportPlaceholderTitle")}
											className="h-11"
										/>
									</FormControl>
									<FormDescription>
										{t("help.supportHintTitle")}
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>
										{t("help.supportFieldDescription")}
									</FormLabel>
									<FormControl>
										<Textarea
											{...field}
											maxLength={2000}
											disabled={mutation.isPending}
											placeholder={t("help.supportDescriptionPrompt")}
											className="min-h-44 resize-y text-base sm:text-sm"
										/>
									</FormControl>
									<div className="flex flex-wrap justify-between gap-2">
										<FormDescription>
											{t("help.supportPrivacyHint")}
										</FormDescription>
										<span className="text-xs tabular-nums text-base-content/70">
											{field.value.length} / 2000
										</span>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormRootMessage />
						<FormActionBar
							className="bg-base-100 backdrop-blur-none"
							lead={
								<span className="text-xs text-base-content/70">
									{t("help.supportRequiredHint")}
								</span>
							}
						>
							<SubmitButton
								pendingLabel={t("help.supportSubmitting")}
								className="min-h-11"
							>
								<Send aria-hidden="true" />
								{t("help.supportSubmit")}
							</SubmitButton>
						</FormActionBar>
					</form>
				</Form>
			</section>
			<aside
				className="space-y-8 px-1 lg:pt-2"
				aria-label={t("help.supportGuidanceTitle")}
			>
				<section>
					<h3 className="text-base font-semibold">
						{t("help.supportGuidanceTitle")}
					</h3>
					<ul className="mt-4 list-disc space-y-3 pl-4 text-sm leading-relaxed text-base-content/75">
						<li>{t("help.supportGuidanceWhere")}</li>
						<li>{t("help.supportGuidanceSteps")}</li>
						<li>{t("help.supportGuidanceExpected")}</li>
					</ul>
				</section>
				<section className="border-t border-base-300 pt-6">
					<h3 className="text-base font-semibold">
						{t("help.supportFollowUpTitle")}
					</h3>
					<p className="mt-2 text-sm leading-relaxed text-base-content/75">
						{t("help.supportFollowUpDescription")}
					</p>
					<Link
						to="/app/help/support/tickets"
						className="mt-3 inline-flex min-h-11 items-center gap-2 rounded text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-primary"
					>
						{t("help.myTickets")}
						<ArrowRight className="size-4" aria-hidden="true" />
					</Link>
				</section>
			</aside>
		</div>
	);
}
